/**
 * RevenueSection — factures / revenus du client (Batch #14.243).
 * Le client saisit ses montants et ses taxes ; aucun rapport de taxes côté portail.
 */
import { useState } from "react";
import axios from "axios";
import { FileText, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders } from "../helpers.js";
import { Field, SectionCard, TaxToggle, fmtCAD, inputCls, isDemoResponse, r2, taxesFromSubtotal, today, addBtnCls, formActionsCls, cancelBtnCls, saveBtnCls, deleteBtnCls } from "./_shared";

const EMPTY = { date: today(), customer: "", description: "", subtotal: "", taxed: true, tps: "", tvq: "", payment_method: "" };

function RevenueForm({ methods, rates, primaryColor, onDone, onCancel }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setSubtotal = (v) => setF((x) => ({ ...x, subtotal: v, ...(x.taxed ? taxesFromSubtotal(v, rates.tps, rates.tvq) : {}) }));
  const setTaxed = (on) => setF((x) => ({ ...x, taxed: on, ...(on ? taxesFromSubtotal(x.subtotal, rates.tps, rates.tvq) : { tps: 0, tvq: 0 }) }));
  const tps = f.taxed ? Number(f.tps) || 0 : 0;
  const tvq = f.taxed ? Number(f.tvq) || 0 : 0;
  const total = r2((Number(f.subtotal) || 0) + tps + tvq);

  const submit = async () => {
    if (!f.date || !(Number(f.subtotal) > 0)) { toast.error("Date et montant requis"); return; }
    setSaving(true);
    try {
      const body = { date: f.date, customer: f.customer, description: f.description, total, tps, tvq, payment_method: f.payment_method };
      const res = await axios.post(`${API}/portal/entrepreneur/revenues`, body, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success("Facture enregistrée");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 rounded-2xl p-4 space-y-3 bg-gray-50/60" style={{ borderColor: primaryColor }} data-testid="ent-portal-revenue-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Date *"><input type="date" className={inputCls} value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="ent-portal-rev-date" /></Field>
        <Field label="Client facturé"><input className={inputCls} value={f.customer} onChange={(e) => set("customer", e.target.value)} placeholder="Nom du client" data-testid="ent-portal-rev-customer" /></Field>
        <Field label="Montant avant taxes *"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.subtotal} onChange={(e) => setSubtotal(e.target.value)} placeholder="0,00" data-testid="ent-portal-rev-subtotal" /></Field>
        <Field label="Mode de paiement">
          <select className={inputCls} value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="ent-portal-rev-method">
            <option value="">—</option>
            {methods.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <TaxToggle checked={f.taxed} onChange={setTaxed} label="J'ai facturé la TPS et la TVQ" testId="ent-portal-rev-taxed" />
        </div>
        {f.taxed && (
          <>
            <Field label="TPS ($)"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.tps} onChange={(e) => set("tps", e.target.value)} data-testid="ent-portal-rev-tps" /></Field>
            <Field label="TVQ ($)"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.tvq} onChange={(e) => set("tvq", e.target.value)} data-testid="ent-portal-rev-tvq" /></Field>
          </>
        )}
        <Field label="Description" className="sm:col-span-2"><input className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Ex. : Facture #1042 — services de mai" data-testid="ent-portal-rev-description" /></Field>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-700">Total facturé : <strong className="font-mono" data-testid="ent-portal-rev-total">{fmtCAD(total)}</strong></div>
        <div className={formActionsCls}>
          <button type="button" onClick={onCancel} className={cancelBtnCls}>Annuler</button>
          <button type="button" onClick={submit} disabled={saving} className={saveBtnCls} style={{ backgroundColor: primaryColor }} data-testid="ent-portal-rev-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer la facture
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RevenueSection({ data, primaryColor, onChanged }) {
  const [adding, setAdding] = useState(false);
  const rates = { tps: data.tps_rate, tvq: data.tvq_rate };
  const remove = async (r) => {
    if (!window.confirm(`Supprimer cette facture de ${fmtCAD(r.total)} ?`)) return;
    try {
      const res = await axios.delete(`${API}/portal/entrepreneur/revenues/${r.id}`, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success("Facture supprimée");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <SectionCard title={`Mes factures ${data.year}`} icon={FileText} primaryColor={primaryColor} testId="ent-portal-revenues"
      action={!adding && (
        <button onClick={() => setAdding(true)} className={addBtnCls} style={{ backgroundColor: primaryColor }} data-testid="ent-portal-rev-add-btn">
          <Plus className="w-4 h-4" /> Ajouter une facture
        </button>
      )}>
      {adding && <div className="mb-4"><RevenueForm methods={data.payment_methods} rates={rates} primaryColor={primaryColor} onDone={() => { setAdding(false); onChanged(); }} onCancel={() => setAdding(false)} /></div>}
      {data.revenues.length === 0 ? (
        <p className="text-sm text-gray-400 italic text-center py-6">Aucune facture pour {data.year}.</p>
      ) : (
        <ul className="divide-y" data-testid="ent-portal-rev-list">
          {data.revenues.map((r) => (
            <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`ent-portal-rev-row-${r.id}`}>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{r.customer || r.description || "Facture"}</div>
                <div className="text-xs text-gray-500">{r.date}{r.customer && r.description ? ` · ${r.description}` : ""}{r.payment_method ? ` · ${r.payment_method}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900 font-mono">{fmtCAD(r.total)}</div>
                {(r.tps > 0 || r.tvq > 0) && <div className="text-[10px] text-gray-400">avant taxes {fmtCAD(r.subtotal)}</div>}
              </div>
              {r.source === "portal" && (
                <button onClick={() => remove(r)} className={deleteBtnCls} title="Supprimer" aria-label="Supprimer la facture" data-testid={`ent-portal-rev-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
